/**
 * Unified Login Component
 * Mobile-optimized, clean login form supporting both PIN and Password
 */

import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import {
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  LockClosedIcon,
  DevicePhoneMobileIcon,
  ArrowRightOnRectangleIcon
} from "@heroicons/react/24/outline";
import { AuthAPI } from "../../api/auth";
import { TokenManager } from "../../api/config";
import { useAuth } from "../../context/AuthContext";

interface LoginFormData {
  identifier: string; // Email or Phone
  password?: string;
  pin?: string;
  loginMethod: 'password' | 'pin';
}

const UnifiedLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'password' | 'pin'>('pin'); 
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for success message from registration
  const successMessage = location.state?.message;
  const prefillEmail = location.state?.email;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      identifier: prefillEmail || "",
      loginMethod: 'pin'
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      let response;
      if (loginMethod === 'pin') {
        response = await AuthAPI.loginWithPin(data.identifier, data.pin!);
      } else {
        response = await AuthAPI.login({
            email: data.identifier,
            password: data.password!
        });
      }

      // Store tokens
      await TokenManager.setToken(response.access_token);
      if (response.refresh_token) {
        await TokenManager.setRefreshToken(response.refresh_token);
      }

      // Refresh global auth state
      await refreshUser();

      // Determine redirect path
      let redirectPath = "/";
      if (response.user?.is_admin || response.user?.is_superadmin) {
        redirectPath = "/p-admin-secure-x9k2m7q4";
      } else if (response.user?.is_shopkeeper) {
        redirectPath = "/dashboard";
      } else {
        redirectPath = location.state?.from?.pathname || "/";
      }

      navigate(redirectPath, { replace: true });
    } catch (error: any) {
      setError(error.error || error.message || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white md:bg-gray-50 flex flex-col md:items-center md:justify-center p-0 md:p-4">
      {/* Mobile Header Background */}
      <div className="md:hidden h-40 bg-gradient-to-br from-blue-600 to-indigo-700 w-full flex flex-col items-center justify-center text-white">
        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-2 shadow-md p-1 border border-slate-100">
          <img src="/logo-icon.svg" alt="Prindox Logo" className="w-12 h-12 object-contain" />
        </div>
        <h1 className="text-xl font-bold tracking-tight">Sign In to Prindox</h1>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white md:rounded-3xl md:shadow-2xl overflow-hidden -mt-6 md:mt-0 rounded-t-3xl z-10 flex-grow md:flex-grow-0"
      >
        <div className="p-8 md:p-10">
          <div className="hidden md:flex flex-col items-center mb-8 text-center">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-md p-1 border border-slate-100">
              <img src="/logo-icon.svg" alt="Prindox Logo" className="w-12 h-12 object-contain" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Welcome Back</h2>
            <p className="text-gray-500 mt-1.5 text-sm">Manage your prints with ease</p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="bg-green-50 border border-green-100 text-green-700 p-4 rounded-2xl flex items-center mb-6 text-sm"
            >
              <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
              {successMessage}
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-2xl flex items-center mb-6 text-sm font-medium"
            >
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3 animate-pulse" />
              {error}
            </motion.div>
          )}

          {/* Login Method Toggle - Segmented Style */}
          <div className="flex p-1 bg-gray-100 rounded-2xl mb-8">
            <button 
              type="button" 
              onClick={() => { setLoginMethod('pin'); setValue('loginMethod', 'pin'); }}
              className={`flex-1 py-2.5 text-sm font-bold rounded-xl transition-all ${
                loginMethod === 'pin' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              PIN Login
            </button>
            <button 
              type="button" 
              onClick={() => { setLoginMethod('password'); setValue('loginMethod', 'password'); }}
              className={`flex-1 py-2.5 text-sm font-bold rounded-xl transition-all ${
                loginMethod === 'password' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Password
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                Email or Phone
              </label>
              <div className="relative group">
                <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                  {...register("identifier", { required: "Email or Phone is required" })}
                  type="text"
                  inputMode="email"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-gray-900 font-medium placeholder:text-gray-400"
                  placeholder="name@example.com"
                />
              </div>
              {errors.identifier && <p className="text-xs text-red-500 mt-1.5 ml-1 font-bold">{errors.identifier.message}</p>}
            </div>

            <AnimatePresence mode="wait">
              {loginMethod === 'pin' ? (
                <motion.div
                  key="pin-field"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                >
                  <div className="flex justify-between items-center mb-1.5 ml-1">
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Your 4-6 Digit PIN
                    </label>
                    <Link to="/forgot-pin" className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:text-blue-700">
                      Forgot PIN?
                    </Link>
                  </div>
                  <div className="relative group">
                    <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      {...register("pin", { 
                        required: loginMethod === 'pin' ? "PIN is required" : false,
                        minLength: { value: 4, message: "Minimum 4 digits" }
                      })}
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-gray-900 font-black tracking-[0.5em] placeholder:tracking-normal placeholder:font-medium"
                      placeholder="****"
                    />
                  </div>
                  {errors.pin && <p className="text-xs text-red-500 mt-1.5 ml-1 font-bold">{errors.pin.message}</p>}
                </motion.div>
              ) : (
                <motion.div
                  key="pass-field"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                >
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                    Password
                  </label>
                  <div className="relative group">
                    <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      {...register("password", { required: loginMethod === 'password' ? "Password is required" : false })}
                      type={showPassword ? "text" : "password"}
                      className="w-full pl-12 pr-12 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-gray-900 font-medium placeholder:text-gray-400"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500 transition-colors p-1"
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500 mt-1.5 ml-1 font-bold">{errors.password.message}</p>}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className={`
                  w-full py-4 px-6 rounded-2xl text-lg font-black uppercase tracking-widest text-white shadow-xl
                  transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100
                  ${isLoading ? 'bg-gray-400' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/25'}
                `}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.062 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing In...
                  </span>
                ) : "Sign In"}
              </button>
            </div>

            <div className="text-center pt-6">
              <p className="text-sm font-medium text-gray-500">
                New to Prindox? 
                <Link
                  to="/register"
                  className="ml-2 text-blue-600 font-black uppercase tracking-wider hover:text-blue-700"
                >
                  Create Account
                </Link>
              </p>
            </div>
          </form>
        </div>
      </motion.div>

      {/* Footer Branding for Mobile */}
      <div className="md:hidden py-8 text-center">
        <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.3em]">Prindox Instant Printing</p>
      </div>
    </div>
  );
};

export default UnifiedLogin;
